import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Plus, Trash2, Package as PackageIcon, Tag, Wrench, BadgePercent } from 'lucide-react';
import { formatIDR } from '../lib/format';
import { ProductSelectorDialog } from './ProductSelectorDialog';
import { PackageSelectorDialog } from './PackageSelectorDialog';
import { NumberInput } from './NumberInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const TypeBadge = ({ type }) => {
  if (type === 'product') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-400/15 text-emerald-300 border border-emerald-400/30">
        <Tag className="h-2.5 w-2.5" />Produk
      </span>
    );
  }
  if (type === 'package_item') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30">
        <PackageIcon className="h-2.5 w-2.5" />Paket
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-white/5 text-muted-foreground border border-white/10">
      <Wrench className="h-2.5 w-2.5" />Custom
    </span>
  );
};

// Numeric input is shared via /components/NumberInput.jsx

export const LineItemsEditor = ({
  items,
  onChange,
  testidPrefix = 'line-item',
  finalDiscount = { type: 'nominal', value: '0' },
  onFinalDiscountChange,
}) => {
  const [productOpen, setProductOpen] = useState(false);
  const [packageOpen, setPackageOpen] = useState(false);

  const addCustom = () => {
    onChange([...items, { id: `tmp-${Date.now()}`, item_name: '', description: '', quantity: 1, unit: '', unit_price: 0, discount: 0, item_type: 'custom' }]);
  };
  const update = (idx, patch) => {
    const next = items.map((it, i) => i === idx ? { ...it, ...patch } : it);
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  const onProductsPicked = (products) => {
    const newItems = products.map(p => ({
      id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      product_id: p.id,
      package_id: null,
      package_group_name: null,
      item_type: 'product',
      item_name: p.product_name,
      description: p.description || '',
      quantity: 1,
      unit: p.unit || '',
      unit_price: p.default_price || 0,
      discount: 0,
    }));
    onChange([...items, ...newItems]);
  };

  const onPackagePicked = (pkg) => {
    const groupName = pkg.package_name;
    const newItems = (pkg.items || []).map(it => ({
      id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      product_id: it.product_id || null,
      package_id: pkg.id,
      package_group_name: groupName,
      item_type: 'package_item',
      item_name: it.item_name || '',
      description: it.description || '',
      quantity: Number(it.quantity) || 0,
      unit: it.unit || '',
      unit_price: Number(it.unit_price) || 0,
      discount: Number(it.discount) || 0,
    }));
    onChange([...items, ...newItems]);
  };

  const computed = useMemo(() => {
    let gross = 0;
    let itemDiscountTotal = 0;
    let subtotal = 0;
    items.forEach(it => {
      const q = parseFloat(it.quantity) || 0;
      const p = parseFloat(it.unit_price) || 0;
      const d = parseFloat(it.discount) || 0;
      const g = q * p;
      gross += g;
      itemDiscountTotal += d;
      subtotal += Math.max(0, g - d);
    });
    const fdType = (finalDiscount?.type || 'nominal').toLowerCase();
    const fdRaw = parseFloat(finalDiscount?.value) || 0;
    let finalDiscountAmount;
    if (fdType === 'percent') {
      const pct = Math.min(100, Math.max(0, fdRaw));
      finalDiscountAmount = Math.round((subtotal * pct / 100) * 100) / 100;
    } else {
      finalDiscountAmount = Math.max(0, fdRaw);
    }
    finalDiscountAmount = Math.min(subtotal, finalDiscountAmount);
    const grandTotal = Math.max(0, subtotal - finalDiscountAmount);
    return { gross, itemDiscountTotal, subtotal, finalDiscountAmount, grandTotal };
  }, [items, finalDiscount]);

  // ---------- Desktop Table Rows ----------
  const renderTableRows = () => {
    const rows = [];
    let lastGroup = '__nogroup__';
    items.forEach((it, idx) => {
      const grp = it.package_group_name || null;
      if (grp && grp !== lastGroup) {
        rows.push(
          <tr key={`grp-${idx}`} className="border-t border-border bg-primary/5">
            <td colSpan={8} className="px-3 py-1.5 text-xs font-semibold text-primary">
              <span className="inline-flex items-center gap-1.5"><PackageIcon className="h-3 w-3" />Paket: {grp}</span>
            </td>
          </tr>
        );
        lastGroup = grp;
      } else if (!grp) {
        lastGroup = '__nogroup__';
      }
      const sub = Math.max(0, (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0) - (parseFloat(it.discount) || 0));
      rows.push(
        <tr key={it.id || idx} className="border-t border-border align-top">
          <td className="px-2 py-1.5">
            <Input
              data-testid={`${testidPrefix}-${idx}-name`}
              className="h-9 min-w-[180px]"
              value={it.item_name}
              title={it.item_name || ''}
              onChange={(e) => update(idx, { item_name: e.target.value })}
              placeholder="Nama item"
            />
            <div className="flex items-center gap-2 mt-1"><TypeBadge type={it.item_type} /></div>
          </td>
          <td className="px-2 py-1.5 align-top">
            <Textarea
              className="min-w-[240px] min-h-[36px] text-sm leading-snug resize-y py-1.5"
              rows={1}
              value={it.description || ''}
              title={it.description || ''}
              onChange={(e) => update(idx, { description: e.target.value })}
              placeholder="Deskripsi (Enter untuk baris baru)"
            />
          </td>
          <td className="px-2 py-1.5"><NumberInput className="h-9 text-right font-mono-num" value={it.quantity} onChange={(v) => update(idx, { quantity: v })} /></td>
          <td className="px-2 py-1.5"><Input className="h-9" value={it.unit || ''} onChange={(e) => update(idx, { unit: e.target.value })} placeholder="Unit" /></td>
          <td className="px-2 py-1.5"><NumberInput className="h-9 text-right font-mono-num" value={it.unit_price} onChange={(v) => update(idx, { unit_price: v })} /></td>
          <td className="px-2 py-1.5"><NumberInput className="h-9 text-right font-mono-num" value={it.discount} onChange={(v) => update(idx, { discount: v })} /></td>
          <td className="px-3 py-1.5 text-right font-mono-num whitespace-nowrap">{formatIDR(sub)}</td>
          <td className="px-2 py-1.5 text-right">
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-red-300" onClick={() => remove(idx)} data-testid={`${testidPrefix}-${idx}-remove`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </td>
        </tr>
      );
    });
    return rows;
  };

  // ---------- Mobile Cards ----------
  const renderMobileCards = () => {
    const blocks = [];
    let lastGroup = '__nogroup__';
    items.forEach((it, idx) => {
      const grp = it.package_group_name || null;
      if (grp && grp !== lastGroup) {
        blocks.push(
          <div key={`grp-m-${idx}`} className="flex items-center gap-2 px-1 pt-1 text-xs font-semibold text-primary">
            <PackageIcon className="h-3.5 w-3.5" />
            <span>Paket: {grp}</span>
          </div>
        );
        lastGroup = grp;
      } else if (!grp) {
        lastGroup = '__nogroup__';
      }
      const q = parseFloat(it.quantity) || 0;
      const p = parseFloat(it.unit_price) || 0;
      const d = parseFloat(it.discount) || 0;
      const sub = Math.max(0, q * p - d);

      blocks.push(
        <div
          key={it.id || `m-${idx}`}
          className="rounded-xl border border-border bg-card/60 p-4 space-y-3 shadow-sm"
          data-testid={`${testidPrefix}-${idx}-mcard`}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <span className="text-xs font-semibold text-muted-foreground">Item #{idx + 1}</span>
              <TypeBadge type={it.item_type} />
            </div>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-9 w-9 shrink-0 text-red-300 hover:text-red-200 hover:bg-red-500/10"
              onClick={() => remove(idx)}
              data-testid={`${testidPrefix}-${idx}-remove-mobile`}
              aria-label="Hapus item"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          {/* Nama Item */}
          <div className="space-y-1.5">
            <Label className="text-xs">Nama Item</Label>
            <Input
              data-testid={`${testidPrefix}-${idx}-name-mobile`}
              className="h-11 md:h-9 text-base md:text-sm"
              value={it.item_name}
              onChange={(e) => update(idx, { item_name: e.target.value })}
              placeholder="Misal: Sound System 5000W"
            />
          </div>

          {/* Deskripsi */}
          <div className="space-y-1.5">
            <Label className="text-xs">Deskripsi</Label>
            <Textarea
              className="min-h-[44px] text-base md:text-sm leading-snug resize-y py-2"
              rows={2}
              value={it.description || ''}
              onChange={(e) => update(idx, { description: e.target.value })}
              placeholder="Opsional — Enter untuk baris baru"
            />
          </div>

          {/* Qty + Satuan */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Qty</Label>
              <NumberInput
                testid={`${testidPrefix}-${idx}-qty-mobile`}
                className="h-11 md:h-9 text-base md:text-sm text-right font-mono-num"
                value={it.quantity}
                onChange={(v) => update(idx, { quantity: v })}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Satuan</Label>
              <Input
                className="h-11 md:h-9 text-base md:text-sm"
                value={it.unit || ''}
                onChange={(e) => update(idx, { unit: e.target.value })}
                placeholder="pcs / hari"
              />
            </div>
          </div>

          {/* Harga */}
          <div className="space-y-1.5">
            <Label className="text-xs">Harga (Rp)</Label>
            <NumberInput
              testid={`${testidPrefix}-${idx}-price-mobile`}
              className="h-11 md:h-9 text-base md:text-sm text-right font-mono-num"
              value={it.unit_price}
              onChange={(v) => update(idx, { unit_price: v })}
            />
            {p > 0 && (
              <p className="text-[11px] text-muted-foreground text-right font-mono-num">
                {formatIDR(p)}
              </p>
            )}
          </div>

          {/* Diskon */}
          <div className="space-y-1.5">
            <Label className="text-xs">Diskon (Rp)</Label>
            <NumberInput
              testid={`${testidPrefix}-${idx}-discount-mobile`}
              className="h-11 md:h-9 text-base md:text-sm text-right font-mono-num"
              value={it.discount}
              onChange={(v) => update(idx, { discount: v })}
            />
            {d > 0 && (
              <p className="text-[11px] text-red-300/80 text-right font-mono-num">
                - {formatIDR(d)}
              </p>
            )}
          </div>

          {/* Subtotal highlight */}
          <div className="rounded-lg border border-primary/30 bg-primary/10 px-3 py-2.5 flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Subtotal</span>
            <span
              className="font-mono-num font-semibold text-base text-primary"
              data-testid={`${testidPrefix}-${idx}-subtotal-mobile`}
            >
              {formatIDR(sub)}
            </span>
          </div>
        </div>
      );
    });
    return blocks;
  };

  return (
    <div className="space-y-3">
      {/* Header + Action buttons */}
      <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:justify-between">
        <Label className="text-xs">Line Items</Label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 md:flex md:flex-wrap">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="h-11 md:h-9 w-full md:w-auto justify-center"
            onClick={() => setProductOpen(true)}
            data-testid={`${testidPrefix}-add-product`}
          >
            <Tag className="h-4 w-4 mr-1.5" />Tambah Produk
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="h-11 md:h-9 w-full md:w-auto justify-center"
            onClick={() => setPackageOpen(true)}
            data-testid={`${testidPrefix}-add-package`}
          >
            <PackageIcon className="h-4 w-4 mr-1.5" />Tambah Paket
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="h-11 md:h-9 w-full md:w-auto justify-center"
            onClick={addCustom}
            data-testid={`${testidPrefix}-add-custom`}
          >
            <Plus className="h-4 w-4 mr-1.5" />Tambah Item Custom
          </Button>
        </div>
      </div>

      {/* DESKTOP: Table */}
      <div className="hidden md:block rounded-lg border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <colgroup>
            <col style={{ minWidth: 200 }} />
            <col style={{ minWidth: 260 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 140 }} />
            <col style={{ width: 130 }} />
            <col style={{ width: 150 }} />
            <col style={{ width: 56 }} />
          </colgroup>
          <thead className="bg-white/5">
            <tr className="text-xs text-muted-foreground">
              <th className="px-3 py-2 text-left">Nama</th>
              <th className="px-3 py-2 text-left">Deskripsi</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-left">Satuan</th>
              <th className="px-3 py-2 text-right">Harga</th>
              <th className="px-3 py-2 text-right">Diskon Item</th>
              <th className="px-3 py-2 text-right">Subtotal</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">Belum ada item. Klik salah satu tombol di atas.</td></tr>
            )}
            {renderTableRows()}
          </tbody>
        </table>
      </div>

      {/* MOBILE: Cards */}
      <div className="md:hidden space-y-3">
        {items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card/40 px-4 py-8 text-center text-sm text-muted-foreground">
            Belum ada item.<br />Tap salah satu tombol di atas untuk menambahkan.
          </div>
        ) : (
          renderMobileCards()
        )}
      </div>

      {/* SHARED: Discount & Totals summary (desktop + mobile) */}
      {items.length > 0 && (
        <div
          className="rounded-xl border border-primary/40 bg-primary/5 p-4 space-y-3"
          data-testid={`${testidPrefix}-summary`}
        >
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Subtotal Sebelum Diskon</span>
            <span className="font-mono-num" data-testid={`${testidPrefix}-summary-subtotal`}>
              {formatIDR(computed.subtotal)}
            </span>
          </div>

          {/* Final Discount controls */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_1fr] gap-2 sm:gap-3 items-end">
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <BadgePercent className="h-3.5 w-3.5" />Diskon Akhir
              </Label>
              <Select
                value={finalDiscount?.type || 'nominal'}
                onValueChange={(v) => onFinalDiscountChange && onFinalDiscountChange({ ...finalDiscount, type: v })}
              >
                <SelectTrigger
                  className="h-11 md:h-9 text-base md:text-sm"
                  data-testid={`${testidPrefix}-final-discount-type`}
                >
                  <SelectValue placeholder="Tipe diskon" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nominal">Nominal (Rp)</SelectItem>
                  <SelectItem value="percent">Persentase (%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">
                {finalDiscount?.type === 'percent' ? 'Nilai (%)' : 'Nilai (Rp)'}
              </Label>
              <NumberInput
                testid={`${testidPrefix}-final-discount-value`}
                className="h-11 md:h-9 text-base md:text-sm text-right font-mono-num"
                value={finalDiscount?.value ?? '0'}
                onChange={(v) => onFinalDiscountChange && onFinalDiscountChange({ ...finalDiscount, value: v })}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Nilai Diskon Akhir</Label>
              <div
                className="h-11 md:h-9 rounded-md border border-border bg-card/40 px-3 flex items-center justify-end font-mono-num text-base md:text-sm text-red-300"
                data-testid={`${testidPrefix}-final-discount-amount`}
              >
                {computed.finalDiscountAmount > 0 ? `- ${formatIDR(computed.finalDiscountAmount)}` : formatIDR(0)}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-primary/30">
            <span className="font-semibold text-base">Grand Total</span>
            <span
              className="font-mono-num font-bold text-lg sm:text-xl text-primary tracking-tight"
              data-testid={`${testidPrefix}-grand-total`}
            >
              {formatIDR(computed.grandTotal)}
            </span>
          </div>
        </div>
      )}

      <ProductSelectorDialog open={productOpen} onOpenChange={setProductOpen} onPicked={onProductsPicked} multi />
      <PackageSelectorDialog open={packageOpen} onOpenChange={setPackageOpen} onPicked={onPackagePicked} />
    </div>
  );
};
